/**
 * Medcor Transport, the public side (M39, ADR-0066). Every route here is dark
 * until go-live: while booking is switched off the server answers 404.
 */
import { apiFetch } from '../lib/api'

export type TransportService =
  | 'standard_ride'
  | 'pts'
  | 'car_hire'
  | 'executive'

/** The backend's `Mobility`. */
export type Mobility =
  | 'walks_unaided'
  | 'walks_with_help'
  | 'wheelchair'
  | 'stretcher'

export type EligibilityState =
  | 'recommended'
  | 'allowed'
  | 'needs_caregiver'
  | 'phone_only'
  | 'disabled'

/** One row of the server's matrix (spec §C.3.2): a mobility, with or without
 * oxygen, and each service's state for it. */
export type EligibilityRow = {
  mobility: Mobility
  uses_oxygen: boolean
  services: Partial<
    Record<TransportService, { state: EligibilityState; reason: string | null }>
  >
}

export type TransportOptions = {
  services: Array<{
    service: TransportService
    min_notice_minutes: number | null
    bookable: boolean
    phone_only: boolean
    from_price: {
      per_leg_pesewas: number | null
      half_day_pesewas: number | null
      full_day_pesewas: number | null
    } | null
  }>
  eligibility: EligibilityRow[]
  areas: Array<{
    id: string
    name: string
    zone: number
    travel_minutes: number
  }>
  hospitals: Array<{ id: string; name: string; zone: number }>
  max_days_ahead: number
  pickup_early_arrival_minutes: number
  pickup_change_window_minutes: number
  caregiver_seats: number
  min_unaccompanied_age: number
}

/**
 * What can be booked (M38-T08). The answer is also the switch (M39-T09): a
 * 404 means booking is off and `/transport` stays the M34 holding page.
 */
export async function fetchTransportOptions(
  signal?: AbortSignal,
): Promise<TransportOptions> {
  const body = await apiFetch<{ data: TransportOptions }>(
    '/api/v1/transport/options',
    signal ? { signal } : {},
  )
  if (!Array.isArray(body?.data?.eligibility)) {
    throw new Error('Unexpected transport options')
  }
  return body.data
}

/**
 * The safety check stopped a booking (M39-T08, spec §C.3.1): one more on
 * today's count. No body, so nothing about the person or their answers is
 * sent. Fire and forget: the stop screen never waits for it, and a failure is
 * silent. `keepalive` lets the count survive the phone handing over to the
 * dialler on the same tap.
 */
export function countGateStop(): void {
  try {
    void apiFetch('/api/v1/transport/gate-stops', {
      method: 'POST',
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Nothing: bookkeeping must never reach the person booking.
  }
}

/** What the customer has chosen so far, enough to price it (backend `Trip`).
 * It never carries a price: the server prices the trip itself. */
export type TripChoice = {
  service: TransportService
  pickup_area_id: string
  hospital_id: string
  journey?: 'one_way' | 'return'
  hire_length?: 'half' | 'full'
  wheelchair_needed?: boolean
  companion?: 'none' | 'caregiver' | 'nurse'
}

/** Why a trip cannot be priced online (backend `quote.Reason`). */
export type QuoteReason =
  | 'phone_only'
  | 'nurse_escort_unavailable'
  | 'phone_quote'
  | 'unknown_area'
  | 'unknown_hospital'
  | 'hospital_zone_missing'
  | 'tariff_missing'

export type TransportQuote = {
  bookable: boolean
  reason?: QuoteReason | string
  zone?: number
  lines?: Array<{
    item: string
    label: string
    amount_pesewas: number
    free: boolean
  }>
  total_pesewas?: number
}

/** Price a trip (M38-T08): its zone and lines, or why not. */
export async function fetchQuote(
  trip: TripChoice,
  signal?: AbortSignal,
): Promise<TransportQuote> {
  const body = await apiFetch<{ data: TransportQuote }>(
    '/api/v1/transport/quote',
    signal
      ? { method: 'POST', body: trip, signal }
      : { method: 'POST', body: trip },
  )
  return body.data
}

/** The full or closed days for a service in [from, to] ("YYYY-MM-DD"). */
export async function fetchFullDays(
  service: TransportService,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const query = new URLSearchParams({ service, from, to })
  const body = await apiFetch<{ data: { full_days: string[] } }>(
    `/api/v1/transport/availability?${query.toString()}`,
    signal ? { signal } : {},
  )
  return body.data.full_days
}

/** What Pay sends (M39-T04a's `CheckoutRequest`). Never a price: the server
 * prices the trip itself. */
export type CheckoutBody = {
  trip: TripChoice
  mobility: Mobility
  uses_oxygen: boolean
  gate_passed_at: string
  booker_first_name: string
  booker_last_name: string
  booker_phone: string
  passenger_is_booker: boolean
  passenger_name: string | null
  passenger_age: number | null
  passenger_phone: string | null
  own_equipment: Array<'own_wheelchair' | 'own_oxygen'>
  driver_notes: string | null
  consent_given: boolean
  pickup_address: string
  pickup_landmark: string | null
  pickup_lat: number | null
  pickup_lng: number | null
  appointment_at: string | null
  pickup_at: string
  return_mode: 'fixed' | 'call_to_collect' | null
  return_pickup_at: string | null
  hire_stops: string | null
  terms_accepted: true
  manage_key: string
}

export type CheckoutOpened = {
  reference: string
  checkout_expires_at: string
  total_pesewas: number
  payment_url: string | null
}

/**
 * Pay (M39-T04b): hold a place and start the payment. The idempotency key
 * makes a second tap or a retry return the same checkout; the manage key,
 * which the page made, is what polls its state (T05). Refusals arrive as
 * `ApiError`s with the server's code and message.
 */
export async function openCheckout(
  body: CheckoutBody,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<CheckoutOpened> {
  const res = await apiFetch<{ data: CheckoutOpened }>(
    '/api/v1/transport/checkouts',
    {
      method: 'POST',
      body,
      headers: { 'Idempotency-Key': idempotencyKey },
      ...(signal ? { signal } : {}),
    },
  )
  return res.data
}

export type CheckoutState = {
  state: 'waiting' | 'not_completed' | 'confirmed' | 'closed'
  expires_at: string | null
}

// ------------------------------------------- the private booking page (M40)

/** The customer's own booking (M40-T08, spec §C.3.6). First names only. */
export type BookingPage = {
  reference: string
  status:
    | 'confirmed'
    | 'driver_assigned'
    | 'picked_up'
    | 'arrived'
    | 'return_requested'
    | 'return_picked_up'
    | 'completed'
    | 'cancelled_by_customer'
    | 'cannot_fulfil'
    | 'no_show'
  service: TransportService
  pickup_at: string
  pickup_area: string
  hospital: string
  booker_first_name: string
  passenger_first_name: string | null
  return_mode: 'fixed' | 'call_to_collect' | null
  return_pickup_at: string | null
  /** "Driver details by …": the pickup less the assignment cut-off. */
  driver_details_by: string
  driver: {
    name: string
    phone: string | null
    vehicle_plate: string | null
    attendant_name: string | null
  } | null
  hospital_informed: boolean
  return_requested: boolean
  receipt: {
    fare_lines: Array<{
      item: string
      label: string
      amount_pesewas: number
      free?: boolean
    }>
    total_pesewas: number
    payment_method: string | null
    paid_at: string | null
  }
  confirmed_at: string | null
}

/**
 * The private link (M40, README): the manage key rides in the URL
 * **fragment**, which a browser never sends to a server, its logs or a
 * referrer. The page reads it and sends it as the `X-Transport-Key` header.
 */
export function bookingPath(reference: string, key: string): string {
  return `/transport/booking/${encodeURIComponent(reference)}#k=${encodeURIComponent(key)}`
}

export function receiptPath(reference: string, key: string): string {
  return `/transport/booking/${encodeURIComponent(reference)}/receipt#k=${encodeURIComponent(key)}`
}

/** The key from a `#k=…` fragment, or null. */
export function keyFromHash(hash: string): string | null {
  const key = new URLSearchParams(hash.replace(/^#/, '')).get('k')
  return key ? key : null
}

/** GET the booking page (M40-T08). The key travels in a header only. */
export async function fetchBookingPage(
  reference: string,
  manageKey: string,
  signal?: AbortSignal,
): Promise<BookingPage> {
  const res = await apiFetch<{ data: BookingPage }>(
    `/api/v1/transport/bookings/${encodeURIComponent(reference)}`,
    {
      headers: { 'X-Transport-Key': manageKey },
      ...(signal ? { signal } : {}),
    },
  )
  return res.data
}

/** "Ready to collect" (M40-T08): a second tap changes nothing and answers the
 * same. A refusal is an ApiError with its code (too_early, not_now, …). */
export async function readyToCollect(
  reference: string,
  manageKey: string,
): Promise<BookingPage> {
  const res = await apiFetch<{ data: BookingPage }>(
    `/api/v1/transport/bookings/${encodeURIComponent(reference)}/ready-to-collect`,
    { method: 'POST', headers: { 'X-Transport-Key': manageKey } },
  )
  return res.data
}

/** Where a checkout stands (M39-T05). The manage key travels in a header,
 * never in the URL. */
export async function fetchCheckoutState(
  reference: string,
  manageKey: string,
  signal?: AbortSignal,
): Promise<CheckoutState> {
  const res = await apiFetch<{ data: CheckoutState }>(
    `/api/v1/transport/checkouts/${encodeURIComponent(reference)}`,
    {
      headers: { 'X-Transport-Key': manageKey },
      ...(signal ? { signal } : {}),
    },
  )
  return res.data
}
